import { Component, DOCUMENT, inject } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Neighbourhood } from './model/story/neighbourhood.model';
import { StoryTheme } from './model/story/story-theme.model';
import { StoryTag } from './model/story/story-tag.model';
import { AppStateStore } from './store/app-state.store';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { NeighbourhoodService } from './services/neighbourhood.service';
import { ThemeService } from './services/theme.service';
import { TagService } from './services/tag.service';
import { combineLatest, debounceTime, distinctUntilChanged, filter, shareReplay, startWith } from 'rxjs';
import { ResponsiveService } from './services/responsive.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatIcon } from '@angular/material/icon';
import { map } from 'rxjs/operators';
import { StoryService } from './services/story.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { FiltersBottomSheet } from './components/filters-bottom-sheet/filters-bottom-sheet';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';

@Component({
  selector: 'app-root',
  imports: [
    MatToolbar,
    RouterOutlet,
    MatFormField,
    MatLabel,
    ReactiveFormsModule,
    MatInput,
    MatSelect,
    MatOption,
    NgClass,
    AsyncPipe,
    MatButton,
    MatProgressBar,
    MatIcon,
    RouterLink,
    MatMiniFabButton,
    RouterLinkActive,
    MatButtonToggleGroup,
    MatButtonToggle,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  /**
   * Dependencies
   */
  public readonly appStateStore = inject(AppStateStore);
  public readonly matBottomSheet = inject(MatBottomSheet);
  private readonly neighbourhoodService = inject(NeighbourhoodService);
  private readonly themeService = inject(ThemeService);
  private readonly tagService = inject(TagService);
  public readonly responsiveService = inject(ResponsiveService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly storyService = inject(StoryService);
  // State
  public showFilterHeader$ = combineLatest([
    this.router.events.pipe(
      // Filter for NavigationEnd
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      // Seed with the current URL immediately
      startWith(this.router.url),
    ),
    this.responsiveService.isMobileScreen$,
  ]).pipe(
    map(([url, isMobile]) => {
      // Robust URL check
      const isStoryPage = url.includes('/story/');
      const showHeader = !isStoryPage;

      // Hide if it's a story page OR if it's mobile
      return showHeader && !isMobile;
    }),
    // Ensure we don't spam the UI with the same result
    distinctUntilChanged(),
    shareReplay(1),
  );

  /**
   * Filters
   */
    // Search query
  public searchQueryInput: FormControl = new FormControl();
  private searchQuery = '';
  // Neighbourhood filter
  public neighbourhoodSelect = new FormControl<Neighbourhood[] | null>([]);
  public neighbourhoodList: Neighbourhood[] = [];
  private selectedAreaIdList: string[] = [];
  // Theme filter
  public storyThemeSelect = new FormControl<StoryTheme[] | null>([]);
  public storyThemeList: StoryTheme[] = [];
  private selectedThemeIdList: string[] = [];
  // Tag filter
  public tagSelect = new FormControl<StoryTag[] | null>([]);
  public storyTagList: StoryTag[] = [];
  private selectedTagIdList: string[] = [];
  // Story type
  public storyTypeInput: FormControl = new FormControl('all');
  private storyType = 'all';
  // Active filter
  public activeInput: FormControl = new FormControl('all');
  private active = 'all';
  // Reset state
  private resetFiltersInProgress = false;

  /**
   * Data
   */

  constructor() {
    this.loadStoryData();
    // Filters
    this.loadFiltersData();
    this.observeFilterChanges();
    // Open Linked Data
    this.publishJsonLdOnStoryOpen();
  }

  /**
   * Filters
   */
  private loadFiltersData(): void {
    // Neighbourhood
    this.neighbourhoodService.getNeighbourhoodList()
      .subscribe((neighbourhoodList: Neighbourhood[]): Neighbourhood[] => this.neighbourhoodList = neighbourhoodList);
    // Theme
    this.themeService.getThemeList()
      .subscribe((storyThemeList: StoryTheme[]): StoryTheme[] => this.storyThemeList = storyThemeList);
    // Tag
    this.tagService.getTagList()
      .subscribe((storyTagList: StoryTag[]): StoryTag[] => this.storyTagList = storyTagList);
  }

  private observeFilterChanges(): void {
    // Search query
    this.searchQueryInput.valueChanges.pipe(debounceTime(200)).subscribe((value: string) => {
      this.searchQuery = value;
      this.loadStoryData();
    });
    // Neighbourhood
    this.neighbourhoodSelect.valueChanges.subscribe((selectedNeighbourhoodList: Neighbourhood[] | null): void => {
      if (selectedNeighbourhoodList !== null) {
        this.selectedAreaIdList = selectedNeighbourhoodList.map(neighbourhood => neighbourhood.id);
        this.loadStoryData();
      }
    });
    // Theme
    this.storyThemeSelect.valueChanges.subscribe((selectedThemeIdList: StoryTheme[] | null): void => {
      if (selectedThemeIdList !== null) {
        this.selectedThemeIdList = selectedThemeIdList.map((storyTheme: StoryTheme) => storyTheme.id);
        this.loadStoryData();
      }
    });
    // Tag
    this.tagSelect.valueChanges.subscribe((selectedTagList: StoryTag[] | null): void => {
      if (selectedTagList !== null) {
        this.selectedTagIdList = selectedTagList.map((tag: StoryTag) => tag.id);
        this.loadStoryData();
      }
    });
    // Story Type
    this.storyTypeInput.valueChanges.subscribe((value: string) => {
      this.storyType = value;
      this.loadStoryData();
    });
    // Active
    this.activeInput.valueChanges.subscribe((value: string) => {
      this.active = value;
      this.loadStoryData();
    });
  }

  public openFiltersSheet(): void {
    this.matBottomSheet.open(FiltersBottomSheet);
  }

  /**
   * Clear all filters
   */
  public clearFilters(): void {
    this.resetFiltersInProgress = true;
    this.searchQueryInput.reset('', {emitEvent: false});
    this.neighbourhoodSelect.reset([], {emitEvent: false});
    this.storyThemeSelect.reset([], {emitEvent: false});
    this.tagSelect.reset([], {emitEvent: false});
    this.storyTypeInput.reset('all', {emitEvent: false});
    this.activeInput.reset('all', {emitEvent: false});
    this.resetFiltersInProgress = false;

    // Also reset the value holder
    this.searchQuery = '';
    this.selectedAreaIdList = [];
    this.selectedThemeIdList = [];
    this.selectedTagIdList = [];
    this.storyType = 'all';
    this.active = 'all';

    // Reload data
    this.loadStoryData();
  }

  /**
   * Load filtered data from the back-end
   */
  public loadStoryData(): void {
    // Avoid loading when restoring the filters
    if (this.resetFiltersInProgress) {
      return;
    }
    // General filters and pagination
    const params: any = {
      search: this.searchQuery,
      'areas[]': this.selectedAreaIdList,
      'themas[]': this.selectedThemeIdList,
      'tags[]': this.selectedTagIdList,
    };

    if (this.storyType !== 'all') {
      params['project'] = this.storyType === 'projects';
    }

    if (this.active !== 'all') {
      params['active'] = this.active === 'active'
    }

    // Load data
    this.appStateStore.loadStoryListData({params});
  }

  /**
   * When not showing a story, remove the related Open Linked Data schema
   */
  private publishJsonLdOnStoryOpen(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd): void => {
        if (event.urlAfterRedirects.includes('/story/') === false) {
          // Remove old JSON-LD if exists
          const old: HTMLElement | null = this.document.getElementById('dynamic-jsonld');
          if (old) old.remove();
        }
      });
  }

}
